import React, { useContext, useEffect, useMemo } from 'react';

import { Tabs } from 'antd';
import Axios from 'axios';

import { ListContext } from '@contexts/ListContext';
import { useUserGuiConfig, useUserGuiConfigDispatch } from '@contexts/UserGuiConfigContext';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { useContactFeatures } from '@hooks/API/Contact/UseContactFeatures';
import { updateModelFeatures } from '@hooks/API/Contact/UseContactsList';
import { useGlobalFeatureSets } from '@hooks/API/UseFeatures';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import FeaturesGroup from '@components/Features/FeaturesGroup';
import DraggableTabs from '@components/Gui/DraggableTabs';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';

import { FeatureSetContainer } from './FeaturesUI';

const { TabPane } = Tabs;

const FeatureSet = ({ featureSet, contactFeatures, readOnly, currentContact }) => {
    return featureSet.FeatureGroups.map((featureGroup, i) => {
        return (
            <FeaturesGroup
                key={featureGroup.ID}
                readOnly={readOnly}
                featuresGroup={featureGroup}
                contactFeatures={contactFeatures}
                contact={currentContact}
                contactType={currentContact.ContactType}
            />
        );
    });
};

export const Features = ({ currentContact, readOnly }) => {
    const { data: contactFeatures } = useContactFeatures(currentContact.ID);
    const { data: globalFeatureSets, isFetching: fetchingGlobalFeatureSets } = useGlobalFeatureSets();
    const { cachedList } = useContext(ListContext);

    const userGuiConfig = useUserGuiConfig();
    const ugc_dispatch = useUserGuiConfigDispatch();

    const departments = useMemo(() => {
        const departments = [];
        if (currentContact) {
            for (const accountingGroup of cachedList.accountingGroups.items) {
                for (const networkAgency of accountingGroup.NetworkAgencies) {
                    for (const department of networkAgency.Departments) {
                        if (currentContact.Departments.find((item) => item.DepartmentID === department.ID)) {
                            departments.push(department);
                        }
                    }
                }
            }
        }
        return departments;
    }, [cachedList, currentContact]);

    const availableFeaturesSets = useMemo(() => {
        if (!fetchingGlobalFeatureSets) {
            const featureSets = [];
            for (const department of departments) {
                for (const featureSetId of department.FeatureSets) {
                    if (!featureSets.find((featureSet) => featureSet.ID === featureSetId)) {
                        const featureSet =
                            globalFeatureSets && globalFeatureSets.find((featureSet) => featureSet.ID === featureSetId);
                        featureSets.push(featureSet);
                    }
                }
            }
            return featureSets;
        }

    }, [departments, globalFeatureSets]);

    const handleSetFeatureOrders = (orders) => {
        ugc_dispatch({
            type: 'Update',
            value: {
                ...userGuiConfig,
                featureTabsOrders: {
                    ...userGuiConfig.featureTabsOrders,
                    [currentContact.ID]: orders,
                },
            },
        });
    };

    const contactFeatureTabOrders =
        userGuiConfig && userGuiConfig.featureTabsOrders && userGuiConfig.featureTabsOrders[currentContact.ID]
            ? userGuiConfig.featureTabsOrders[currentContact.ID]
            : [];

    if (contactFeatures && !fetchingGlobalFeatureSets && availableFeaturesSets && availableFeaturesSets.length > 0) {
        if (availableFeaturesSets.length > 1) {
            return (
                <DraggableTabs orders={contactFeatureTabOrders} onUpdateOrders={!readOnly && handleSetFeatureOrders}>
                    {availableFeaturesSets.map((featureSet) => (
                        <TabPane tab={featureSet.Name} key={featureSet.ID}>
                            <FeatureSetContainer>
                                <FeatureSet
                                    readOnly={readOnly}
                                    contactFeatures={contactFeatures}
                                    featureSet={featureSet}
                                    currentContact={currentContact}
                                />
                            </FeatureSetContainer>
                        </TabPane>
                    ))}
                </DraggableTabs>
            );
        } else {
            const featureSet = availableFeaturesSets[0];
            return (
                <FeatureSetContainer>
                    <FeatureSet
                        readOnly={readOnly}
                        contactFeatures={contactFeatures}
                        featureSet={featureSet}
                        currentContact={currentContact}
                    />
                </FeatureSetContainer>
            );
        }
    } else {
        return <Loading />;
    }
};

const FeaturesDrawer = ({ contact, readOnly, onClose }) => {
    const breakpoints = useNwBreakPoints();

    const updateModel = async () => {
        forceReloadContact(contact.ID);
        const updatedSimpleContact = await Axios.get(`/models/simple/${contact.ID}`);
        updateModelFeatures(contact.ID, updatedSimpleContact.FT);
    };

    return (
        <NwDrawer
            padding="0"
            whiteBg={breakpoints === 1}
            onClose={async () => {
                await updateModel();
                onClose();
            }}
            title="Contact features"
        >
            <div className="drawer-content standard">
                <Features readOnly={readOnly} currentContact={contact} />
            </div>
        </NwDrawer>
    );
};

export default FeaturesDrawer;
